import React, { useContext, useState } from 'react';
import { Button, Divider, Input, Modal, Row, Tooltip, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePageId } from 'contexts/PageIdContext';
import { useSurvey } from 'contexts/SurveyContext';
import { Bitrix24Context } from 'contexts/legacy/Bitrix24Context';
import { Answer, EventType, SurveyResponsePayload } from 'types/survey';
import { Button as ButtonStyled } from 'views/MainPage/Subscription/styles';
import { LimitedCheckboxGroup } from './LimitedCheckboxGroup';
import { CheckCircleOutlined } from '@ant-design/icons';

const langMap: Record<string, string> = {
	br: 'pt',
	la: 'es',
	en: 'en',
};

export default function SurveyModal() {
	const { surveyAvailable, sendSurveyResponse, shouldOpen, sendSurveyEvent, setShouldOpen } =
		useSurvey();
	const [responses, setResponses] = useState<Record<string, any>>({});
	const [errors, setErrors] = useState<Record<string, boolean>>({});
	const [loadingSave, setLoadingSave] = useState(false);
	const { activePageId } = usePageId();
	const intl = useIntl();

	const bitrix24 = useContext(Bitrix24Context);
	const { lang } = bitrix24;

	function normalizeLang(lang: string): string {
		return langMap[lang] || 'en';
	}

	const getTextByLang = (obj?: Record<string, string> | null): string => {
		if (!obj) return '';
		const rawLang = lang?.split('-')[0] || 'pt';
		const normalizedLang = langMap[rawLang] || 'en';
		return obj[normalizedLang] || obj['pt'] || Object.values(obj)[0] || '';
	};

	const onClose = () => {
		setShouldOpen(false);
		setResponses({});
		setErrors({});
	};

	const handleChange = (id: string, value: any) => {
		setResponses((prev) => ({ ...prev, [id]: value }));
		setErrors((prev) => ({ ...prev, [id]: false }));
	};

	const validate = () => {
		if (!surveyAvailable) return false;

		let valid = true;
		const newErrors: Record<string, boolean> = {};

		surveyAvailable.questions.forEach((q) => {
			const value = responses[q.id];

			if (q.isRequired) {
				if (q.type === 'text' && (typeof value !== 'string' || value.trim().length === 0)) {
					newErrors[q.id] = true;
					valid = false;
				}

				if (q.type === 'multi' && (!Array.isArray(value) || value.length === 0)) {
					newErrors[q.id] = true;
					valid = false;
				}
			}
		});

		setErrors(newErrors);
		return valid;
	};

	const formatPayload = (): SurveyResponsePayload => {
		const answers: Answer[] = Object.entries(responses)
			.map(([questionId, value]) => {
				const question = surveyAvailable?.questions.find((q) => q.id === questionId);
				if (!question) return null;

				if (!question.isRequired && (!value || (Array.isArray(value) && value.length === 0))) {
					return null;
				}

				const base = {
					questionId: question.id,
					questionText: getTextByLang(question.title),
				};

				if (question.type === 'text' && typeof value === 'string') {
					return {
						...base,
						type: 'text' as const,
						answerText: value,
					};
				}

				if (question.type === 'multi' && Array.isArray(value)) {
					return {
						...base,
						type: 'multi' as const,
						answerMulti: value.map((optId: string) => {
							const opt = question.options?.find((o) => o.id === optId);
							return {
								optionSelectedId: opt?.id,
								optionSelectedText: opt ? getTextByLang(opt.text) : '',
							};
						}),
					};
				}
				return null;
			})
			.filter((a): a is Answer => a !== null);

		return {
			surveyId: surveyAvailable?.id,
			language: normalizeLang(lang),
			answers,
		};
	};

	const saveResponse = async () => {
		if (!validate()) return;

		try {
			setLoadingSave(true);

			const payload = formatPayload();

			await sendSurveyResponse(payload);
			onClose();
		} catch (error) {
			console.error('Erro ao salvar resposta:', error);
		} finally {
			setLoadingSave(false);
		}
	};

	if (!surveyAvailable) return null;

	const showModal =
		(shouldOpen &&
			!!surveyAvailable &&
			(surveyAvailable.displayConfig.displayFormat === 'fakedoor' ||
				(surveyAvailable.displayConfig.displayFormat === 'popup' &&
					surveyAvailable.displayConfig.applicationPage?.includes(activePageId ?? '')))) ??
		shouldOpen;

	const { title, description, questions, displayConfig, isAnswered, canDiscard } = surveyAvailable;

	if (displayConfig.displayFormat === 'fakedoor' && isAnswered) {
		return (
			// @ts-ignore
			<Modal
				closable
				visible={shouldOpen && !!surveyAvailable}
				onCancel={onClose}
				width={450}
				footer={null}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						textAlign: 'center',
						padding: 6,
						minHeight: '300px',
						gap: 12,
					}}
				>
					<CheckCircleOutlined style={{ color: '#43C565', fontSize: '80px' }} />
					<Typography.Title level={4}>
						<FormattedMessage id="survey_answered_title" />
					</Typography.Title>
					<Typography.Text>
						<FormattedMessage id="survey_answered_description" />
					</Typography.Text>
					<Button onClick={onClose}>
						<FormattedMessage id="survey_modal_close_button" />
					</Button>
				</div>
			</Modal>
		);
	}

	const canDiscardSurvey = displayConfig.displayFormat === 'popup' && canDiscard;

	const sendEvent = (eventType: EventType) => {
		sendSurveyEvent(eventType);
	};

	const footer = (
		<div
			style={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				gap: 4,
			}}
		>
			{canDiscardSurvey ? (
				<Tooltip arrowPointAtCenter title={<FormattedMessage id="survey_modal_discard_tooltip" />}>
					<Button onClick={() => sendEvent(EventType.discarded)}>
						<FormattedMessage id="survey_modal_discard_button" />
					</Button>
				</Tooltip>
			) : (
				<div></div>
			)}
			<div
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					gap: 4,
				}}
			>
				{displayConfig.displayFormat === 'popup' ? (
					<Button onClick={() => sendEvent(EventType.ignored)}>
						<FormattedMessage id="survey_modal_redisplay_button" />
					</Button>
				) : (
					<Button onClick={onClose}>
						<FormattedMessage id="survey_modal_close_button" />
					</Button>
				)}
				<ButtonStyled type="primary" onClick={saveResponse} loading={loadingSave}>
					&nbsp;
					<FormattedMessage id="survey_send_response_button" />
				</ButtonStyled>
			</div>
		</div>
	);

	return (
		// @ts-ignore
		<Modal visible={showModal} onCancel={onClose} width={750} footer={footer} title={null}>
			<Row>
				<Typography.Title level={4}>{getTextByLang(title)}</Typography.Title>
			</Row>
			<Row>
				<Typography.Text>{getTextByLang(description)}</Typography.Text>
			</Row>
			<Row>
				<Typography.Text type="secondary">
					<FormattedMessage id="survey_modal_alert_required_fields" />
				</Typography.Text>
			</Row>
			<Divider />
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-start',
					width: '100%',
					gap: 24,
				}}
			>
				{questions.map((question, index) => (
					<div
						key={question.id}
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-start',
							width: '100%',
							gap: 2,
						}}
					>
						<Typography.Text strong>
							{index + 1} - {getTextByLang(question.title)} {question.isRequired && '*'}
						</Typography.Text>
						<Typography.Text>{getTextByLang(question.description)}</Typography.Text>
						{question.type === 'text' && (
							<>
								<Input
									onChange={(e) => handleChange(question.id, e.target.value)}
									value={responses[question.id] || ''}
									autoComplete="off"
									required={question.isRequired}
								/>
								{errors[question.id] && (
									<Typography.Text type="danger">
										{errors[question.id]
											? intl.formatMessage({ id: 'survey_input_required_error' })
											: undefined}
									</Typography.Text>
								)}
							</>
						)}
						{question.type === 'multi' && (
							<LimitedCheckboxGroup
								options={(question.options ?? []).map((opt) => ({
									...opt,
									text: getTextByLang(opt.text),
								}))}
								maxSelections={question.maxSelections}
								value={responses[question.id] || []}
								onChange={(val) => handleChange(question.id, val)}
								error={errors[question.id] ?? false}
								helperText={
									question.maxSelections === 1
										? intl.formatMessage({ id: 'survey_helper_text_input_1' })
										: intl.formatMessage({ id: 'survey_helper_text_input_2' })
								}
							/>
						)}
					</div>
				))}
			</div>
		</Modal>
	);
}
